import React, { useCallback, useEffect, useRef, useState } from 'react'
import { httpsCallable } from 'firebase/functions'
import { getFns } from '../../../firebase'
import Button from '../../../components/ui/Button'
import Card, { CardHeader, CardTitle, CardDescription, CardContent } from '../../../components/ui/Card'
import { StoreNetworkResult } from '../../../components/StoreNetworkStatus'

const addressLines = (value) => value.split(/\r?\n/).map((line) => line.trim()).filter(Boolean)

export default function StoreNetworkSettings() {
  const [saved, setSaved] = useState(null)
  const [draft, setDraft] = useState('')
  const [result, setResult] = useState(null)
  const [busy, setBusy] = useState('load')
  const [error, setError] = useState('')
  const [notice, setNotice] = useState('')
  const [conflict, setConflict] = useState(false)
  const mountedRef = useRef(false)
  const requestRef = useRef(0)

  const load = useCallback(async ({ preserveDraft = false } = {}) => {
    const request = ++requestRef.current
    setBusy('load')
    setError('')
    setNotice('')
    try {
      const { data } = await httpsCallable(getFns(), 'storeNetworkAdminGet')({})
      if (!mountedRef.current || request !== requestRef.current) return
      if (!Array.isArray(data?.addresses) || !Number.isInteger(data?.revision)) throw new Error('Invalid settings response')
      setSaved(data)
      setResult(data)
      if (!preserveDraft) setDraft(data.addresses.join('\n'))
      else setNotice('Latest saved addresses loaded below. Your edits are unchanged. Review them before saving.')
      setConflict(false)
    } catch {
      if (!mountedRef.current || request !== requestRef.current) return
      setResult({ status: 'unavailable' })
      setError('Could not load store network settings. Please try again.')
    } finally {
      if (mountedRef.current && request === requestRef.current) setBusy('')
    }
  }, [])

  useEffect(() => {
    mountedRef.current = true
    load()
    return () => { mountedRef.current = false; ++requestRef.current }
  }, [load])

  const checkNetwork = async (useCurrentIp = false) => {
    const request = ++requestRef.current
    setBusy(useCurrentIp ? 'use-current' : 'check')
    setError('')
    setNotice('')
    setResult(null)
    try {
      const { data } = await httpsCallable(getFns(), 'storeNetworkCheck')({})
      if (!mountedRef.current || request !== requestRef.current) return
      setResult(data)
      if (useCurrentIp) {
        if (!data?.currentIp) {
          setError('The current internet address is unavailable. Nothing was added.')
          return
        }
        const addresses = addressLines(draft)
        if (!addresses.includes(data.currentIp)) {
          if (addresses.length >= 8) {
            setError('Use no more than 8 addresses. Remove an address before adding this connection.')
            return
          }
          setDraft([...addresses, data.currentIp].join('\n'))
        }
        setNotice('Current IP added to the form. Confirm you are on the store network, then save to apply it.')
      }
    } catch {
      if (!mountedRef.current || request !== requestRef.current) return
      setResult({ status: 'unavailable' })
      setError('Could not verify this connection. Please try again.')
    } finally {
      if (mountedRef.current && request === requestRef.current) setBusy('')
    }
  }

  const save = async (event) => {
    event.preventDefault()
    if (!saved || busy || conflict) return
    const addresses = addressLines(draft)
    setError('')
    setNotice('')
    if (addresses.length > 8) {
      setError('Use no more than 8 addresses, one per line.')
      return
    }
    const request = ++requestRef.current
    setBusy('save')
    try {
      const { data } = await httpsCallable(getFns(), 'storeNetworkAdminSave')({ addresses, revision: saved.revision })
      if (!mountedRef.current || request !== requestRef.current) return
      if (!Array.isArray(data?.addresses) || !Number.isInteger(data?.revision)) throw new Error('Invalid settings response')
      setSaved(data)
      setResult(data)
      setDraft(data.addresses.join('\n'))
      setNotice(data.addresses.length ? 'Store network settings saved.' : 'Settings saved. No connections will be approved until an address is added.')
    } catch (err) {
      if (!mountedRef.current || request !== requestRef.current) return
      if (err?.code === 'functions/aborted' || err?.code === 'functions/already-exists') {
        setConflict(true)
        setError('Another administrator changed these settings. Your edits are still here. Review the latest saved addresses before saving.')
      } else if (err?.code === 'functions/invalid-argument') {
        setError(err.message || 'Check the addresses and try again.')
      } else {
        setError('Could not confirm the save. Your edits are still here. Review the latest settings before trying again.')
        setConflict(true)
      }
    } finally {
      if (mountedRef.current && request === requestRef.current) setBusy('')
    }
  }

  return <Card variant="solid" hover="none">
    <CardHeader>
      <CardTitle>Store Network</CardTitle>
      <CardDescription>Save the store internet addresses so the portal can check whether a connection matches.</CardDescription>
    </CardHeader>
    <CardContent className="mt-6 space-y-6">
      <div className="rounded-lg border border-border p-4">
        <StoreNetworkResult result={result} checking={['load', 'check', 'use-current'].includes(busy)} />
        {result?.currentIp && <p className="mt-3 break-all text-xs text-muted-foreground">Current public IP: <span className="font-mono text-foreground">{result.currentIp}</span></p>}
        <Button type="button" variant="outline" size="sm" iconName="RefreshCw" onClick={() => saved ? checkNetwork() : load()} disabled={Boolean(busy)} className="mt-3">{saved ? 'Check network' : 'Retry'}</Button>
      </div>

      <form onSubmit={save} className="space-y-4">
        <div>
          <label htmlFor="store-network-addresses" className="block text-sm font-medium text-foreground">Approved store internet addresses</label>
          <p id="store-network-help" className="mt-1 text-xs leading-relaxed text-muted-foreground">Connect to the store Wi-Fi before using the current IP. Each address applies only after you save.</p>
          <textarea
            id="store-network-addresses"
            aria-describedby="store-network-help store-network-format"
            value={draft}
            onChange={(event) => { setDraft(event.target.value); setNotice('') }}
            disabled={!saved || Boolean(busy)}
            rows={5}
            spellCheck={false}
            autoCapitalize="none"
            autoCorrect="off"
            className="mt-3 block w-full resize-y rounded-lg border border-border bg-background p-3 font-mono text-base text-foreground focus:outline-none focus:ring-2 focus:ring-ring disabled:opacity-50 md:text-sm"
          />
          <p id="store-network-format" className="mt-2 text-xs leading-relaxed text-muted-foreground">Up to 8 entries, one per line. Use exact IPv4 or IPv6 addresses. Only add an IPv6 /64 network confirmed in the router settings or by the network administrator; it is never inferred automatically. An empty list will not approve any connection.</p>
          <Button type="button" variant="outline" size="sm" iconName="Plus" className="mt-3" onClick={() => checkNetwork(true)} disabled={!saved || Boolean(busy)} loading={busy === 'use-current'}>Use current IP</Button>
        </div>

        {error && <div role="alert" className="rounded-lg border border-destructive/30 bg-destructive/10 p-3 text-sm text-destructive">{error}</div>}
        {notice && <p role="status" className="text-sm text-foreground">{notice}</p>}
        {conflict && <Button type="button" variant="outline" onClick={() => load({ preserveDraft: true })} disabled={Boolean(busy)}>Review latest settings</Button>}

        {saved && <div className="rounded-lg bg-muted/40 p-3">
          <p className="text-xs font-medium text-foreground">Saved addresses</p>
          {saved.addresses.length ? <ul className="mt-2 space-y-1 text-xs text-muted-foreground">{saved.addresses.map((address) => <li key={address} className="break-all font-mono">{address}</li>)}</ul> : <p className="mt-1 text-xs text-muted-foreground">No addresses saved.</p>}
        </div>}

        <Button type="submit" iconName="Save" loading={busy === 'save'} disabled={!saved || Boolean(busy) || conflict}>Save store network</Button>
      </form>

      <p className="border-t border-border pt-4 text-xs leading-relaxed text-muted-foreground">This check identifies an internet connection, not a Wi-Fi name or physical location. VPNs, Private Relay, or an internet provider change may affect the result and require an administrator to review the saved addresses.</p>
    </CardContent>
  </Card>
}
